import React from 'react';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import Verified from '@mui/icons-material/Verified';
import HourglassFull from '@mui/icons-material/HourglassFull';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import { Box, Tooltip, Chip, Typography } from '@mui/material';

import { COLUMN_LABELS, COLUMN_TYPES, FIELD_GROUPS } from './constants';

export const getColumnLabel = (key, customLabels = {}) =>
  customLabels[key] || COLUMN_LABELS[key] || key;

export const formatDate = (date) => {
  if (!date) return '';
  const raw = typeof date === 'object' && date?.date ? date.date : date;
  if (!raw) return '';

  const text = String(raw).trim();
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const mxMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})(.*)$/);
  if (mxMatch) {
    return `${mxMatch[1]}/${mxMatch[2]}/${mxMatch[3]}`;
  }

  const d = new Date(text);
  if (isNaN(d.getTime())) return raw;

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatCellValue = (key, value) => {
  if (key === 'FOLIO_FACTURA') {
    const str = (value ?? '').toString().trim();
    if (!str || str === '0') return 'Sin folio';
    return str;
  }
  if (key === 'EMPRESA') {
    const str = (value ?? '').toString().trim().toUpperCase();
    const map = {
      'ESEASA CONSTRUCCIONES': 'CONSTRUCCIONES',
      'ESEASA OFFSHORE': 'OFFSHORE',
      'PESADO TRANSPORT': 'PESADO',
      'GIMSA': 'GIMSA',
    };
    return map[str] || value || '';
  }
  if (key === 'FECHA_FACTURA') {
    if (!value) return 'Sin fecha';
    return formatDate(value);
  }
  if (key === 'PAGO' || key === 'TIPO_PAGO') {
    const v = (value ?? '').toString().toUpperCase().trim();
    if (v === 'CONTADO') return 'Contado';
    if (v === 'CREDITO') return 'Crédito';
    if (!v || v === '0') return 'N/A';
    return v;
  }
  if (key === 'NUMERO_ECONOMICO' || key === 'NUMERO_ECO') {
    const str = (value ?? '').toString().trim();
    return str || 'S/N';
  }
  if (key === 'CONCEPTOPAGO' || key === 'OBSERVACIONES' || key === 'NOTAS') {
    const str = (value ?? '').toString().trim();
    return str || 'S/N';
  }
  if (key === 'CANCELADO_ERP') {
    const n = Number(value);
    if (n === 1) return 'Cancelado';
    if (n === 0) return 'Activo';
    return '';
  }

  const dateFields = ['FECHA', 'FECHA_OC', 'FECHASOLICITUD', 'FECHA_SOLICITUD', 'FECHAPAGO', 'FECHA_FACTURA', 'VENCIMIENTO', 'FECHA_PAGO', 'FECHA_COMPROMISO', 'FECHA_SOLICITUD_PAGO', 'ULTIMA_SOLICITUD'];
  if (dateFields.includes(key)) {
    return formatDate(value);
  }

  const moneyFields = ['PAGOSAUT_IMPORTE', 'MONTO_SOLICITADO', 'MONTOSOLICITADO', 'TOTALOC', 'TOTAL', 'MONTO', 'MONTO_UNIFICADO', 'PENDIENTE', 'RESTANTE', 'PAGADO', 'COSTOU', 'TOTAL_PART', 'PRECIO', 'PRECIO_UNITARIO', 'IMPORTE'];
  if (moneyFields.includes(key)) {
    if (value == null || value === '' || value === 'NaN' || value === 'null' || value === 'undefined') {
      return '$0.00';
    }
    const cleanStr = String(value).replace(/[^0-9.-]/g, '');
    const n = typeof value === 'number' ? value : (cleanStr !== '' ? Number(cleanStr) : NaN);
    if (!isNaN(n)) {
      return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }
    return '$0.00';
  }

  if (key === 'CANTIDAD' && value != null && value !== '') {
    const n = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
    if (!isNaN(n)) {
      return n.toLocaleString('es-MX', { maximumFractionDigits: 2 });
    }
  }

  return value ?? '';
};

export const renderAuthStatus = (value, motivoCancelacion) => {
  if (value === 'N/A') return "N/A";
  const numericValue = Number(value);
  if (numericValue === 1) return <Verified sx={{ color: 'green' }} />;
  if (numericValue === 0) return <HourglassFull sx={{ color: 'orange' }} />;

  switch (value) {
    case 'CHECK_GREEN': return <CheckCircle sx={{ color: 'green' }} />;
    case 'PENDING_YELLOW': return <TimerRoundedIcon sx={{ fontSize: 24, color: 'orange' }} />;
    case 'CANCEL_RED':
      return (
        <Tooltip
          title={motivoCancelacion || 'Sin motivo especificado'}
          arrow
          disableInteractive
          disablePortal
          slotProps={{ tooltip: { sx: { pointerEvents: 'none' } } }}
        >
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Cancel sx={{ color: 'red' }} />
          </Box>
        </Tooltip>
      );
    default:
      return value;
  }
};

export const renderCanceladoERP = (value) => {
  const n = Number(value);
  if (n === 1) return <Cancel sx={{ color: '#d32f2f' }} />;
  if (n === 0) return <CheckCircle sx={{ color: '#2e7d32' }} />;
  return '';
};

export const renderStatusColor = (status, confirmarPrecio, canceladoErp) => {
  if (Number(canceladoErp) === 1) return '#fde8e8';

  switch (status) {
    case 'En Proceso':
    case 'Proceso': return '#fef3c7';
    case 'Solicitud Parcial':
    case 'Solicitada Parcial': return '#e2f4ff';
    case 'Solicitada Total': return '#e9ddfc';
    case 'Pago Parcial':
    case 'Pagada Parcial': return '#ccfbf1'; // Turquesa / Menta un poco mas recio
    case 'Pago Total':
    case 'Pagado Total':
    case 'Pagada Total': return '#e8f5e9'; // Verde esmeralda suave
    case 'Autorizada': return '#e0edff';
    case 'Pagada': return '#e8f5e9';
    case 'Cancelada': return '#fde8e8';
    case 'Cancelada ERP': return '#fde8e8';
    case 'Vencida': return '#ffe8d6';
    case 'Sin solicitud': return '#ffffff';
    case 'Tiene solicitud': return '#e8f5e9';
    default: return '#ffffff';
  }
};

export const getColumnType = (key) => COLUMN_TYPES[key] ?? 'text';

export const renderEstatusChip = (status) => {
  let bg = '#f8fafc';
  let color = '#475569';
  let border = 'rgba(0,0,0,0.08)';

  const displayMap = {
    'Proceso': 'Pendiente',
    'Autorizada': 'Autorizada',
    'Solicitud Parcial': 'Solicitud parcial',
    'Solicitada Parcial': 'Solicitada parcial',
    'Solicitada Total': 'Solicitada total',
    'Pago Parcial': 'Pago parcial',
    'Pagada Parcial': 'Pagada parcial',
    'Pago Total': 'Pagada',
    'Pagado Total': 'Pagada',
    'Pagada Total': 'Pagada total',
    'Pagada': 'Pagada',
    'Cancelada': 'Rechazada',
    'Cancelada ERP': 'Cancelada',
    'Rechazada': 'Rechazada',
    'Vencida': 'Vencida',
    'Default': 'Default'
  };

  const label = displayMap[status] || status || 'Default';

  switch (status) {
    case 'Proceso':
      bg = '#fffbeb'; color = '#92400e'; border = '#fde68a'; break;
    case 'Solicitud Parcial':
    case 'Solicitada Parcial':
      bg = '#e0f2fe'; color = '#0369a1'; border = '#bae6fd'; break;
    case 'Solicitada Total':
      bg = '#ede9fe'; color = '#5b21b6'; border = '#c4b5fd'; break;
    case 'Autorizada':
      bg = '#dbeafe'; color = '#1e40af'; border = '#bfdbfe'; break;
    case 'Pago Parcial':
    case 'Pagada Parcial':
      bg = '#99f6e4'; color = '#0d5d56'; border = '#5eead4'; break; // Menta turquesa mas notorio
    case 'Pago Total':
    case 'Pagado Total':
    case 'Pagada Total':
    case 'Pagada':
    case 'Activo':
      bg = '#dcfce7'; color = '#15803d'; border = '#86efac'; break; // Verde esmeralda
    case 'Inactivo':
      bg = '#fee2e2'; color = '#991b1b'; border = '#fca5a5'; break;
    case 'Cancelada':
      bg = '#fee2e2'; color = '#991b1b'; border = '#fca5a5'; break;
    case 'Rechazada':
      bg = '#fde8e8'; color = '#C62828'; border = '#f5c6c6'; break;
    case 'Cancelada ERP':
      bg = '#3B0000'; color = '#ffffff'; border = '#7B0000'; break;
    case 'Vencida':
      bg = '#fff7ed'; color = '#c2410c'; border = '#fed7aa'; break;
    default: break;
  }

  const isInactivoOrActivo = status === 'Inactivo' || status === 'Activo';

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: bg,
        color: color,
        fontWeight: 700,
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        height: '20px',
        borderRadius: '999px',
        border: `1px solid ${border}`,
        letterSpacing: '0.04em',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        '& .MuiChip-label': { px: 1, py: 0 }
      }}
    />
  );
};

// ─── Modo BORDE: devuelve el color del borde izquierdo según estatus ──────────
export const renderStatusBorderColor = (status, canceladoErp) => {
  if (Number(canceladoErp) === 1) return '#dc2626';
  const u = String(status || '').toUpperCase();
  if (u.includes('PAGADA TOTAL') || u.includes('PAGO TOTAL') || u === 'PAGADA') return '#16a34a';
  if (u.includes('PAGADA PARCIAL') || u.includes('PAGO PARCIAL')) return '#059669';
  if (u.includes('SOLICITADA TOTAL')) return '#7c3aed';
  if (u.includes('SOLICITADA PARCIAL') || u.includes('SOLICITUD PARCIAL')) return '#0891b2';
  if (u.includes('AUTORIZADA')) return '#2563eb';
  if (u.includes('CANCEL') || u.includes('RECHAZADA')) return '#dc2626';
  if (u.includes('VENCIDA')) return '#ea580c';
  if (u.includes('PROCESO')) return '#d97706';
  return '#cbd5e1';
};

// ─── Modo TEMA MÓVIL: devuelve gradientes, bordes y fondos armónicos adaptados al estatus ───
export const getStatusMobileTheme = (status, confirmarPrecio, canceladoErp) => {
  if (Number(canceladoErp) === 1) {
    return {
      primary: '#b91c1c',
      border: '#fca5a5',
      gradient: 'linear-gradient(165deg, #fee2e2 0%, #fef2f2 45%, #fffbfb 100%)',
      subtleBg: '#fef2f2',
    };
  }

  const s = String(status || '').trim();
  const u = s.toUpperCase();

  if (u.includes('PAGADA TOTAL') || u.includes('PAGO TOTAL') || u === 'PAGADA' || u === 'ACTIVO') {
    return {
      primary: '#15803d',
      border: '#86efac',
      gradient: 'linear-gradient(165deg, #dcfce7 0%, #f0fdf4 45%, #f7fdf9 100%)',
      subtleBg: '#f0fdf4',
    };
  }

  if (u.includes('PAGADA PARCIAL') || u.includes('PAGO PARCIAL')) {
    return {
      primary: '#0f766e',
      border: '#5eead4',
      gradient: 'linear-gradient(165deg, #ccfbf1 0%, #f0fdfa 45%, #f6fefc 100%)',
      subtleBg: '#f0fdfa',
    };
  }

  if (u.includes('SOLICITADA TOTAL')) {
    return {
      primary: '#6d28d9',
      border: '#c4b5fd',
      gradient: 'linear-gradient(165deg, #ede9fe 0%, #faf5ff 45%, #fbf9ff 100%)',
      subtleBg: '#faf5ff',
    };
  }

  if (u.includes('SOLICITADA PARCIAL') || u.includes('SOLICITUD PARCIAL')) {
    return {
      primary: '#0369a1',
      border: '#7dd3fc',
      gradient: 'linear-gradient(165deg, #e0f2fe 0%, #f0f9ff 45%, #f8fcff 100%)',
      subtleBg: '#f0f9ff',
    };
  }

  if (u.includes('AUTORIZADA')) {
    return {
      primary: '#1d4ed8',
      border: '#93c5fd',
      gradient: 'linear-gradient(165deg, #dbeafe 0%, #eff6ff 45%, #f8fbff 100%)',
      subtleBg: '#eff6ff',
    };
  }

  if (u.includes('CANCEL') || u.includes('RECHAZADA') || u === 'INACTIVO') {
    return {
      primary: '#b91c1c',
      border: '#fca5a5',
      gradient: 'linear-gradient(165deg, #fee2e2 0%, #fef2f2 45%, #fffbfb 100%)',
      subtleBg: '#fef2f2',
    };
  }

  if (u.includes('VENCIDA')) {
    return {
      primary: '#c2410c',
      border: '#fed7aa',
      gradient: 'linear-gradient(165deg, #ffedd5 0%, #fff7ed 45%, #fffcf8 100%)',
      subtleBg: '#fff7ed',
    };
  }

  if (u.includes('PROCESO')) {
    return {
      primary: '#b45309',
      border: '#fde68a',
      gradient: 'linear-gradient(165deg, #fef3c7 0%, #fffbeb 45%, #fffef7 100%)',
      subtleBg: '#fffbeb',
    };
  }

  // Default / Sin Solicitud
  return {
    primary: '#64748b',
    border: '#cbd5e1',
    gradient: 'linear-gradient(165deg, #f1f5f9 0%, #f8fafc 45%, #ffffff 100%)',
    subtleBg: '#f8fafc',
  };
};

// ─── Modo PASTILLA (Progress Pill): componente con barra de progreso ──────────
export const renderProgressPill = (status) => {
  let percent = 0;
  let bg = '#f1f5f9';
  let color = '#475569';
  let barColor = '#94a3b8';
  let barGlow = 'rgba(148,163,184,0.3)';
  let border = '#e2e8f0';
  let label = 'Sin solicitud';
  let icon = '○';

  const u = String(status || '').toUpperCase();

  if (u.includes('PAGADA TOTAL') || u.includes('PAGO TOTAL') || u === 'PAGADA') {
    percent = 100; bg = '#f0fdf4'; color = '#15803d'; barColor = '#22c55e'; border = '#bbf7d0';
    barGlow = 'rgba(34,197,94,0.25)'; label = 'Pagada'; icon = '✓';
  } else if (u.includes('PAGADA PARCIAL') || u.includes('PAGO PARCIAL')) {
    percent = 75; bg = '#ecfdf5'; color = '#047857'; barColor = '#10b981'; border = '#a7f3d0';
    barGlow = 'rgba(16,185,129,0.25)'; label = 'Pago Parcial'; icon = '◕';
  } else if (u.includes('SOLICITADA TOTAL')) {
    percent = 50; bg = '#f5f3ff'; color = '#6d28d9'; barColor = '#8b5cf6'; border = '#ddd6fe';
    barGlow = 'rgba(139,92,246,0.25)'; label = 'Sol. Total'; icon = '◑';
  } else if (u.includes('SOLICITADA PARCIAL') || u.includes('SOLICITUD PARCIAL')) {
    percent = 25; bg = '#ecfeff'; color = '#0e7490'; barColor = '#06b6d4'; border = '#cffafe';
    barGlow = 'rgba(6,182,212,0.25)'; label = 'Sol. Parcial'; icon = '◔';
  } else if (u.includes('AUTORIZADA')) {
    percent = 15; bg = '#eff6ff'; color = '#1e40af'; barColor = '#60a5fa'; border = '#dbeafe';
    barGlow = 'rgba(96,165,250,0.25)'; label = 'Autorizada'; icon = '◉';
  } else if (u.includes('ERP')) {
    percent = 0; bg = '#1c0000'; color = '#fca5a5'; barColor = '#dc2626'; border = '#7f1d1d';
    barGlow = 'rgba(220,38,38,0.3)'; label = 'Cancel. ERP'; icon = '✕';
  } else if (u.includes('CANCEL') || u.includes('RECHAZADA')) {
    percent = 0; bg = '#fef2f2'; color = '#991b1b'; barColor = '#ef4444'; border = '#fecaca';
    barGlow = 'rgba(239,68,68,0.25)'; label = 'Cancelada'; icon = '✕';
  } else if (u.includes('VENCIDA')) {
    percent = 5; bg = '#fff7ed'; color = '#c2410c'; barColor = '#fb923c'; border = '#fed7aa';
    barGlow = 'rgba(251,146,60,0.25)'; label = 'Vencida'; icon = '⚠';
  } else if (u.includes('PROCESO') || u.includes('PENDIENTE')) {
    percent = 5; bg = '#fffbeb'; color = '#92400e'; barColor = '#f59e0b'; border = '#fde68a';
    barGlow = 'rgba(245,158,11,0.25)'; label = 'Pendiente'; icon = '◌';
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        bgcolor: bg,
        color: color,
        border: `1px solid ${border}`,
        borderRadius: '10px',
        px: '7px',
        pt: '4px',
        pb: '5px',
        minWidth: 95,
        maxWidth: 120,
        boxShadow: `0 1px 3px rgba(0,0,0,0.06), 0 0 0 0 ${barGlow}`,
      }}
    >
      {/* Header: icono + etiqueta + % */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '3px' }}>
        <Typography
          component="span"
          sx={{ fontSize: '0.6rem', lineHeight: 1, flexShrink: 0, opacity: 0.9 }}
        >
          {icon}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.67rem', fontWeight: 800, flex: 1,
            textTransform: 'uppercase', letterSpacing: '0.02em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: '0.6rem', fontWeight: 700, opacity: 0.7, flexShrink: 0, ml: 'auto' }}
        >
          {percent}%
        </Typography>
      </Box>
      {/* Barra de progreso */}
      <Box sx={{ height: 5, width: '100%', bgcolor: 'rgba(0,0,0,0.08)', borderRadius: 99, overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${percent}%`,
            background: percent === 100
              ? `linear-gradient(90deg, ${barColor} 0%, #4ade80 100%)`
              : percent === 0
                ? barColor
                : `linear-gradient(90deg, ${barColor}cc 0%, ${barColor} 100%)`,
            borderRadius: 99,
            boxShadow: percent > 0 ? `0 0 6px ${barGlow}` : 'none',
            transition: 'width 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        />
      </Box>
    </Box>
  );
};



export const normalizeValue = (val, type) => {
  if (val == null) return null;
  switch (type) {
    case 'boolean':
      if (typeof val === 'boolean') return val ? 1 : 0;
      if (typeof val === 'number') return val ? 1 : 0;
      if (typeof val === 'string') return val === '1' || val.toLowerCase() === 'true' ? 1 : 0;
      return 0;
    case 'number': return Number(val);
    case 'date': {
      const raw = typeof val === 'object' && val?.date ? val.date : val;
      const t = new Date(raw).getTime();
      return isNaN(t) ? null : t;
    }
    default: return val?.toString?.().toLowerCase?.() ?? '';
  }
};

// Intenta crear una clave estable por fila; siempre incluye el índice como
// sufijo para garantizar unicidad cuando el mismo ID aparece en múltiples filas.
export const getRowKey = (row, index) => {
  const natural =
    row?.ID ??
    row?.AUTSOLPAGOID ??
    row?.FOLIO ??
    row?.FOLIO_OC ??
    row?.FOLIO_FACTURA ??
    (row?.__idx !== undefined && row?.__idx !== '' ? row.__idx : null);

  if (natural != null && index !== undefined) {
    return `${natural}-${index}`;
  }
  if (natural != null) {
    return String(natural);
  }
  return index !== undefined ? `row-${index}` : `row-${Math.random().toString(36).substr(2, 9)}`;
};

export function orderFields(visibleKeys) {
  const used = new Set();
  const out = [];
  const primary = FIELD_GROUPS.flatMap(g => g.primary || []);

  primary.forEach(k => {
    if (visibleKeys.includes(k) && !used.has(k)) {
      used.add(k); out.push(k);
    }
  });

  FIELD_GROUPS.forEach(g => {
    (g.fields || []).forEach(k => {
      if (visibleKeys.includes(k) && !used.has(k)) {
        used.add(k); out.push(k);
      }
    });
  });

  visibleKeys.forEach(k => {
    if (!used.has(k)) out.push(k);
  });

  return out;
}
